"use client";

import { useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import {
  acceptOpenRequest,
  distanceKmBetween,
  watchOpenRequests,
  watchPartnerProfile,
  type Coordinates,
  type OpenRequest,
  type PartnerProfile,
} from "./dispatch-client";
import { markUserGestured, startDispatchRing, stopDispatchRing } from "./dispatch-alerts";
import { explainQuote, suggestQuote } from "./pricing";

type PartnerAlertBarProps = {
  user: User;
  onOpenDashboard: () => void;
  onToast: (message: string) => void;
};

export default function PartnerAlertBar({ user, onOpenDashboard, onToast }: PartnerAlertBarProps) {
  const [online, setOnline] = useState(false);
  const [profile, setProfile] = useState<PartnerProfile | null>(null);
  const [requests, setRequests] = useState<OpenRequest[]>([]);
  const [skipped, setSkipped] = useState<Record<string, true>>({});
  const [quotes, setQuotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [point, setPoint] = useState<Coordinates | null>(null);
  const locationRef = useRef<Coordinates | null>(null);
  locationRef.current = point;

  useEffect(() => {
    return watchPartnerProfile(user.uid, (next) => {
      setProfile(next);
      setOnline(!!next?.online && !next?.suspended);
    });
  }, [user.uid]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (position) => setPoint({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
      () => undefined,
      { enableHighAccuracy: true, maximumAge: 8000, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  useEffect(() => {
    if (!online) {
      setRequests([]);
      stopDispatchRing();
      return;
    }
    return watchOpenRequests(() => locationRef.current, 25, setRequests, user.uid);
  }, [online, user.uid]);

  useEffect(() => {
    if (!point) return;
    setRequests((prev) => prev
      .map((row) => ({ ...row, distanceKm: Math.round(distanceKmBetween(point, row.coordinates) * 10) / 10 }))
      .filter((row) => row.distanceKm <= 25)
      .sort((left, right) => (left.distanceKm ?? 999) - (right.distanceKm ?? 999)));
  }, [point]);

  const visible = requests.filter((row) => !skipped[row.id]);
  const alertKey = `${visible.length}:${visible[0]?.id || ""}`;

  useEffect(() => {
    if (!online || !visible.length) {
      stopDispatchRing();
      return;
    }
    const top = visible[0];
    startDispatchRing(
      visible.length,
      `${top.problem} · ${top.vehicle} · ${top.distanceKm ?? "?"} km`,
    );
  }, [online, alertKey]);

  const accept = async (request: OpenRequest) => {
    markUserGestured();
    if (!point) {
      onToast("Allow location first");
      return;
    }
    setBusy(true);
    try {
      const suggested = suggestQuote({
        basePrice: profile?.basePrice ?? 299,
        distanceKm: request.distanceKm,
      });
      const quote = Number(quotes[request.id]) || suggested.total;
      await acceptOpenRequest(request.id, quote, point);
      stopDispatchRing();
      onToast(`Accepted at ₹${quote} — opening dashboard`);
      onOpenDashboard();
    } catch (error) {
      onToast(error instanceof Error ? error.message : "Could not accept — maybe another mistri took it");
    } finally {
      setBusy(false);
    }
  };

  if (!online) return null;

  if (!visible.length) {
    return (
      <div className="partner-online-strip">
        You are ONLINE · waiting for nearby SOS · keep this tab open
      </div>
    );
  }

  return (
    <div className="sos-overlay" role="dialog" aria-modal="true" aria-label="Nearby customer requests">
      <div className="sos-overlay-inner">
        <header className="sos-overlay-head">
          <span className="sos-pulse-dot" />
          <div>
            <h2>{visible.length} customer{visible.length === 1 ? "" : "s"} need help</h2>
            <p>Pick any — tap Accept. Ring stops when you accept or skip all.</p>
          </div>
        </header>

        <div className="sos-overlay-list">
          {visible.map((request) => {
            const suggested = suggestQuote({
              basePrice: profile?.basePrice ?? 299,
              distanceKm: request.distanceKm,
            });
            const edited = Number(quotes[request.id]) || 0;
            return (
              <article key={request.id} className="sos-card">
                <div className="sos-card-main">
                  <b>{request.problem} · {request.vehicle}</b>
                  <small>{request.distanceKm ?? "?"} km · {request.locationLabel}</small>
                  <span className="offer-calc">{explainQuote(suggested)}</span>
                </div>
                <div className="sos-card-actions">
                  <input
                    type="number"
                    min="49"
                    aria-label="Your quote"
                    value={quotes[request.id] ?? String(suggested.total)}
                    onChange={(e) => setQuotes({ ...quotes, [request.id]: e.target.value })}
                  />
                  <button className="primary" disabled={busy} onClick={() => void accept(request)}>
                    Accept ₹{edited || suggested.total}
                  </button>
                  <button
                    className="offer-skip"
                    disabled={busy}
                    onClick={() => {
                      markUserGestured();
                      setSkipped((prev) => ({ ...prev, [request.id]: true }));
                    }}
                  >
                    Skip
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        <button className="sos-overlay-more" onClick={() => { markUserGestured(); onOpenDashboard(); }}>
          Open full mistri dashboard →
        </button>
      </div>
    </div>
  );
}
